import { useQuery } from '@tanstack/react-query';

import axios from '@/portainer/services/axios/axios';
import { buildUrl } from '@/react/portainer/registries/queries/build-url';
import { queryKeys } from '@/react/portainer/registries/queries/query-keys';
import { EnvironmentId } from '@/react/portainer/environments/types';
import { RegistryId } from '@/react/portainer/registries/types/registry';

// Repository listings change rarely and the catalog call is the wizard's slowest
// step, so keep it cached long enough to survive navigating between the steps.
const REPOSITORIES_CACHE_TIME = 8 * 60 * 60 * 1000;

// A registry the wizard cannot reach must not hold the form. The server bounds
// the listing too, generously enough for a slow registry that does answer; this
// is the shorter bound a suggestion list can afford, after which the field says
// to type the path instead.
const REPOSITORIES_TIMEOUT = 10 * 1000;

/**
 * useAddonRegistryRepositories lists a registry's repositories so the install
 * wizard can offer the one holding the add-on's Helm chart.
 *
 * Deliberately unfiltered. Asking the API for Helm charts only (its `mediaType`
 * filter) costs three extra requests per repository, because a repository's
 * artifact type is only visible inside its manifests — several hundred round
 * trips on an ordinary registry. Listing every repository is a single catalog
 * call, the same one the registry browse view makes, and the version lookup
 * rejects a repository that turns out not to be a chart.
 *
 * It reads that call directly rather than through useRegistryRepositories, for
 * the two things the wizard needs and the browse view does not: a bound on how
 * long a suggestion list may take, and no error notification — a registry that
 * will not list its repositories is answered on the field, which stays typeable.
 * The query key is the shared one, so a listing either view already fetched is
 * reused.
 */
export function useAddonRegistryRepositories(
  registryId?: RegistryId,
  environmentId?: EnvironmentId
) {
  return useQuery({
    queryKey: queryKeys.repositoriesList(registryId ?? 0),
    queryFn: () => getRepositories(registryId ?? 0, environmentId ?? 0),
    enabled: !!registryId && !!environmentId,
    cacheTime: REPOSITORIES_CACHE_TIME,
    // An unreachable registry is not a transient fault, and retrying only makes
    // the field wait a multiple of the bound above before saying so.
    retry: false,
  });
}

async function getRepositories(
  registryId: RegistryId,
  endpointId: EnvironmentId
) {
  const { data } = await axios.get<Array<string>>(
    buildUrl(registryId, 'repositories'),
    { params: { endpointId }, timeout: REPOSITORIES_TIMEOUT }
  );

  return data;
}
